import {
  type ChangeEvent, type DragEvent, type ReactElement, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import Panel from 'components/Panel/Panel';
import { AppRoutes } from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';

import {
  hasImportErrors,
  parseWatchCompanyImportJson,
} from './importWatchCompanies';
import {
  TargetedMarketingGate,
  TargetedMarketingHeader,
} from './TargetedMarketingChrome';
import useWatchTowerMock from './useWatchTowerMock';

import type { ImportIssue, WatchCompanyImportFile } from './importWatchCompanies';

import styles from './scss/TargetedMarketing.module.scss';

function WatchTowerImport(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    state, liveActions,
  } = useWatchTowerMock();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [issues, setIssues] = useState<ImportIssue[]>([]);
  const [payload, setPayload] = useState<WatchCompanyImportFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const errorCount = issues.filter((item) => item.severity === 'error').length;
  const warningCount = issues.filter((item) => item.severity === 'warning').length;
  const canCommit = Boolean(payload) && !hasImportErrors(issues);

  const applyFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const report = parseWatchCompanyImportJson(text, file.name, state);
      setFileName(file.name);
      setIssues(report.issues);
      setPayload(hasImportErrors(report.issues) ? null : report.payload);
    };
    reader.readAsText(file);
  };

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      applyFile(file);
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      applyFile(file);
    }
  };

  const onConfirm = async () => {
    if (!payload || hasImportErrors(issues)) {
      return;
    }
    try {
      await liveActions.importFile(payload);
      toast.success(t('targetedMarketing.importCommitted'));
      navigate(AppRoutes.watchTower);
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    }
  };

  return (
    <TargetedMarketingGate>
      <div className={styles.root}>
        <div className={styles.wrapper}>
          <TargetedMarketingHeader
            title={t('targetedMarketing.importTitle')}
            backTo={AppRoutes.watchTower}
            backLabel={t('targetedMarketing.watchTowerTitle')}
            actions={null}
          />

          <Panel className={styles.panel}>
            <input
              ref={inputRef}
              className={styles.hiddenFile}
              type="file"
              accept=".json,application/json"
              onChange={onPick}
            />
            <div
              className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''}`}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
            >
              <p className={styles.dropTitle}>{t('targetedMarketing.importDrop')}</p>
              <p className={styles.muted}>{t('targetedMarketing.importJsonOnly')}</p>
              <Button
                title={t('targetedMarketing.importPick')}
                variant={BUTTON_VARIANT.secondary}
                onClick={() => inputRef.current?.click()}
              />
              {fileName ? <p className={styles.fileName}>{fileName}</p> : null}
            </div>
          </Panel>

          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.validationReport')}</h2>
            {fileName && issues.length === 0 ? (
              <p className={styles.muted}>{t('targetedMarketing.importClean')}</p>
            ) : null}
            {fileName && issues.length > 0 ? (
              <p className={styles.muted}>
                {t('targetedMarketing.importIssueCounts', { errors: errorCount, warnings: warningCount })}
              </p>
            ) : null}
            {!fileName ? (
              <EmptyList text={t('targetedMarketing.importEmptyReport')} />
            ) : null}
            {issues.length > 0 ? (
              <div className={styles.tableWrap}>
                <table className={`${styles.table} ${styles.tableCompact}`}>
                  <thead>
                    <tr>
                      <th>{t('targetedMarketing.importPath')}</th>
                      <th>{t('targetedMarketing.importRow')}</th>
                      <th>{t('targetedMarketing.importField')}</th>
                      <th>{t('targetedMarketing.importMessage')}</th>
                      <th>{t('targetedMarketing.severity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.map((item) => (
                      <tr key={`${item.path}-${item.field}-${item.message}`}>
                        <td>{item.path}</td>
                        <td>{item.row ?? t('targetedMarketing.none')}</td>
                        <td>{item.field}</td>
                        <td>{item.message}</td>
                        <td>
                          <span className={
                            item.severity === 'error' ? styles.severityError : styles.severityWarning
                          }
                          >
                            {item.severity}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <div className={styles.footerActions}>
              <Button
                title={t('targetedMarketing.importConfirm')}
                disabled={!canCommit}
                onClick={onConfirm}
              />
              <Button
                title={t('common.cancel')}
                variant={BUTTON_VARIANT.secondary}
                onClick={() => navigate(AppRoutes.watchTower)}
              />
            </div>
          </Panel>
        </div>
      </div>
    </TargetedMarketingGate>
  );
}

export default WatchTowerImport;
